import React, { useEffect, useMemo, useState } from 'react';
import { Users, Crown, Loader2, AlertCircle, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';
import { organizationService } from '../services/OrganizationService';
import { useAuth } from '../contexts/AuthContext';

// A member of the caller's organization. Mirrors the shape returned by
// GET /api/organization/members (see OrganizationSettingsPanel's Member type).
interface Member {
  id: number;
  uid: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  joined_at?: string;
}

// Two-letter initials for the avatar fallback (same convention as the sidebar).
const initials = (nameOrEmail: string) =>
  (nameOrEmail || 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

/**
 * Team page — lists the members of the current user's organization so a user can
 * see who is on their team without opening the full Organization settings panel.
 * Read-only: member management (add/remove) stays in Organization settings, which
 * is admin-gated. The members endpoint only returns the full roster to admins, so
 * for non-admins we fall back to showing at least the current user.
 */
const TeamPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const [members, setMembers] = useState<Member[]>([]);
  const [orgName, setOrgName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [infoRes, membersRes] = await Promise.all([
          organizationService.getOrganizationInfo().catch(() => null),
          organizationService.getOrganizationMembers().catch(() => null),
        ]);
        if (cancelled) return;
        const org = infoRes?.data?.organization;
        if (org?.name) setOrgName(org.name);
        const list: Member[] = membersRes?.data?.members ?? [];
        setMembers(list);
      } catch (err) {
        if (!cancelled) setError(t('team.error', { defaultValue: 'Failed to load your team.' }));
        // eslint-disable-next-line no-console
        console.error('Failed to load organization members:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Non-admins may get an empty roster from the backend — always show at least the
  // current user so the page is never blank for a legitimate org member.
  const displayMembers = useMemo<Member[]>(() => {
    if (members.length > 0) return members;
    if (currentUser) {
      return [
        {
          id: 0,
          uid: currentUser.uid || 'me',
          name: currentUser.name || currentUser.displayName || currentUser.email || 'You',
          email: currentUser.email || '',
          role: currentUser.role || 'member',
          is_active: true,
        },
      ];
    }
    return [];
  }, [members, currentUser]);

  return (
    <AppLayout pageTitle={t('header.team', { defaultValue: 'Team' })}>
      <MobileHeader title={t('header.team', { defaultValue: 'Team' })} />
      <div className="flex-1 overflow-auto bg-main-bg min-h-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-8 sm:py-10">
          {/* Header */}
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-gray-900 leading-tight">
                {t('team.title', { defaultValue: 'Team' })}
              </h1>
              {orgName && (
                <p className="text-sm text-gray-500 flex items-center gap-1.5 truncate">
                  <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                  {orgName}
                </p>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-6 mt-2">
            {t('team.subtitle', { defaultValue: 'People in your organization.' })}
          </p>

          {/* States */}
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-16">
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('team.loading', { defaultValue: 'Loading your team…' })}
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {displayMembers.length}{' '}
                  {displayMembers.length === 1
                    ? t('team.memberLabel', { defaultValue: 'member' })
                    : t('team.membersLabel', { defaultValue: 'members' })}
                </span>
              </div>
              <div className="space-y-2">
                {displayMembers.map((member) => {
                  const isAdmin = member.role === 'admin';
                  const isMe = currentUser && member.uid === currentUser.uid;
                  return (
                    <div
                      key={member.uid}
                      className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-5 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-gray-700 leading-none">
                            {initials(member.name || member.email)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {member.name || member.email}
                            {isMe && (
                              <span className="ml-2 text-xs font-normal text-gray-400">
                                {t('team.you', { defaultValue: '(you)' })}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{member.email}</p>
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                          isAdmin ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {isAdmin ? (
                          <span className="flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            {t('organization.admin', { defaultValue: 'Admin' })}
                          </span>
                        ) : (
                          t('organization.member', { defaultValue: 'Member' })
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default TeamPage;
